/**
 * Item Data Loader
 *
 * Loads and caches loot and consumable items from JSON catalogs.
 * Follows the same pattern as classDataLoader.ts for consistency.
 */

import { LootItem, ConsumableItem, AnyItem, LootSubtype, ConsumableSubtype } from '../types/equipment';
import lootData from '../data/equipment/loot.json';
import consumablesData from '../data/equipment/consumables.json';

/**
 * Item Data Loader class
 * Singleton pattern for loading and caching equipment items
 */
class ItemDataLoader {
  private lootCache: Map<string, LootItem> = new Map();
  private consumableCache: Map<string, ConsumableItem> = new Map();
  private initialized = false;

  /**
   * Initialize caches with data from JSON files
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Load loot items
      const { loot } = lootData as { loot: LootItem[] };
      loot.forEach((item: LootItem) => {
        this.lootCache.set(item.id, item);
      });

      // Load consumable items
      const { consumables } = consumablesData as { consumables: ConsumableItem[] };
      consumables.forEach((item: ConsumableItem) => {
        this.consumableCache.set(item.id, item);
      });

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize item loader:', error);
      throw error;
    }
  }

  /**
   * Find an item by ID (searches both loot and consumables)
   * @param itemId - The unique item ID
   * @returns The item if found, null otherwise
   */
  findItemById(itemId: string): AnyItem | null {
    // Check loot cache first
    const loot = this.lootCache.get(itemId);
    if (loot) return loot;

    // Check consumable cache
    const consumable = this.consumableCache.get(itemId);
    return consumable || null;
  }

  /**
   * Get all loot items
   * @returns Array of all loot items
   */
  getAllLoot(): LootItem[] {
    return Array.from(this.lootCache.values());
  }

  /**
   * Get all consumable items
   * @returns Array of all consumable items
   */
  getAllConsumables(): ConsumableItem[] {
    return Array.from(this.consumableCache.values());
  }

  /**
   * Filter loot items by subtype
   * @param subtype - The loot subtype to filter by
   * @returns Array of matching loot items
   */
  getLootBySubtype(subtype: string): LootItem[] {
    return Array.from(this.lootCache.values()).filter(
      (item) => item.subtype === subtype
    );
  }

  /**
   * Filter consumable items by subtype
   * @param subtype - The consumable subtype to filter by
   * @returns Array of matching consumable items
   */
  getConsumableBySubtype(subtype: string): ConsumableItem[] {
    return Array.from(this.consumableCache.values()).filter(
      (item) => item.subtype === subtype
    );
  }

  /**
   * Get all items (both loot and consumables)
   * @returns Array of all items
   */
  getAllItems(): AnyItem[] {
    return [...this.getAllLoot(), ...this.getAllConsumables()];
  }

  /**
   * Search items by name (case-insensitive)
   * @param query - The search query
   * @returns Array of matching items
   */
  searchByName(query: string): AnyItem[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllItems().filter((item) =>
      item.name.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Search items by tag
   * @param tag - The tag to search for
   * @returns Array of items with matching tag
   */
  searchByTag(tag: string): AnyItem[] {
    const lowerTag = tag.toLowerCase();
    return this.getAllItems().filter((item) =>
      item.tags.some((t) => t.toLowerCase() === lowerTag)
    );
  }

  /**
   * Filter items by tier
   * @param tier - The tier to filter by (1-4)
   * @returns Array of items available at or below the tier
   */
  getItemsByTier(tier: 1 | 2 | 3 | 4): AnyItem[] {
    return this.getAllItems().filter(
      (item) => !item.tier || item.tier <= tier
    );
  }

  /**
   * Get items by effect type
   * @param effectType - The effect type to filter by
   * @returns Array of items with matching effect type
   */
  getItemsByEffectType(effectType: string): AnyItem[] {
    return this.getAllItems().filter((item) =>
      item.effects.some((effect) => effect.effectType === effectType)
    );
  }

  /**
   * Get loot item by ID
   * @param itemId - The loot item ID
   * @returns The loot item if found, null otherwise
   */
  getLootById(itemId: string): LootItem | null {
    return this.lootCache.get(itemId) || null;
  }

  /**
   * Get consumable item by ID
   * @param itemId - The consumable item ID
   * @returns The consumable item if found, null otherwise
   */
  getConsumableById(itemId: string): ConsumableItem | null {
    return this.consumableCache.get(itemId) || null;
  }

  /**
   * Check if an item exists
   * @param itemId - The item ID to check
   * @returns True if item exists, false otherwise
   */
  itemExists(itemId: string): boolean {
    return this.lootCache.has(itemId) || this.consumableCache.has(itemId);
  }

  /**
   * Get the total count of items
   * @returns Object with loot, consumable, and total counts
   */
  getItemCounts(): { loot: number; consumables: number; total: number } {
    const loot = this.lootCache.size;
    const consumables = this.consumableCache.size;
    return {
      loot,
      consumables,
      total: loot + consumables,
    };
  }

  /**
   * Clear cache (for testing)
   */
  clearCache(): void {
    this.lootCache.clear();
    this.consumableCache.clear();
    this.initialized = false;
  }
}

// Export singleton instance
export const itemLoader = new ItemDataLoader();

// Export class for testing
export { ItemDataLoader };
